import { ThumbnailTypes, ReturnPolicy } from '@rbx/thumbnails';
import Asset from '../enums/Asset';
import Item from '../enums/Item';
import {
  BADGE_LEARN_MORE_URL,
  DEVELOPER_PRODUCT_LEARN_MORE_URL,
  PASS_LEARN_MORE_URL,
  SALE_LOCATION_LEARN_MORE_URL,
  SUBSCRIPTION_LEARN_MORE_URL,
  TOKEN_LEARN_MORE_URL,
} from './linkConstants';

export const assetTypeToItemType: { [key in Asset]: Item } = {
  [Asset.Place]: Item.Game,
  [Asset.SharedExperiences]: Item.Game,
  [Asset.MyExperiences]: Item.Game,
  [Asset.ShareLink]: Item.Game,
  [Asset.TShirt]: Item.CatalogAsset,
  [Asset.Shirt]: Item.CatalogAsset,
  [Asset.Pants]: Item.CatalogAsset,
  [Asset.Hat]: Item.CatalogAsset,
  [Asset.HairAccessory]: Item.CatalogAsset,
  [Asset.FaceAccessory]: Item.CatalogAsset,
  [Asset.NeckAccessory]: Item.CatalogAsset,
  [Asset.ShoulderAccessory]: Item.CatalogAsset,
  [Asset.FrontAccessory]: Item.CatalogAsset,
  [Asset.BackAccessory]: Item.CatalogAsset,
  [Asset.WaistAccessory]: Item.CatalogAsset,
  [Asset.TShirtAccessory]: Item.CatalogAsset,
  [Asset.ShirtAccessory]: Item.CatalogAsset,
  [Asset.PantsAccessory]: Item.CatalogAsset,
  [Asset.JacketAccessory]: Item.CatalogAsset,
  [Asset.SweaterAccessory]: Item.CatalogAsset,
  [Asset.ShortsAccessory]: Item.CatalogAsset,
  [Asset.DressSkirtAccessory]: Item.CatalogAsset,
  [Asset.EmoteAnimation]: Item.CatalogAsset,
  [Asset.EyebrowAccessory]: Item.CatalogAsset,
  [Asset.EyelashAccessory]: Item.CatalogAsset,
  [Asset.FaceMakeup]: Item.CatalogAsset,
  [Asset.LipMakeup]: Item.CatalogAsset,
  [Asset.EyeMakeup]: Item.CatalogAsset,
  [Asset.AllCatalogAsset]: Item.CatalogAsset,
  [Asset.Decal]: Item.LibraryAsset,
  [Asset.Image]: Item.LibraryAsset,
  [Asset.Audio]: Item.LibraryAsset,
  [Asset.Model]: Item.LibraryAsset,
  [Asset.Mesh]: Item.LibraryAsset,
  [Asset.MeshPart]: Item.LibraryAsset,
  [Asset.Plugin]: Item.LibraryAsset,
  [Asset.Animation]: Item.LibraryAsset,
  [Asset.FontFamily]: Item.LibraryAsset,
  [Asset.Video]: Item.LibraryAsset,
  [Asset.Event]: Item.Event,
  [Asset.PastEvent]: Item.Event,
  [Asset.DraftEvent]: Item.Event,
  [Asset.UpcomingEvent]: Item.Event,
  [Asset.StorePreviewVideo]: Item.LibraryAsset,
  [Asset.GamePreviewVideo]: Item.LibraryAsset,
};

export const itemTypeToPath: { [key in Item]: string } = {
  [Item.Game]: 'experiences',
  [Item.CatalogAsset]: 'catalog',
  [Item.LibraryAsset]: 'library',
  [Item.Bundle]: 'bundle',
  [Item.Badge]: 'badges',
  [Item.GamePass]: 'passes',
  [Item.DeveloperProduct]: 'developer-products', // TODO this doesn't actually work for urlservice
  [Item.TranslatorGame]: 'experiences',
  [Item.Places]: 'places',
  [Item.CreatedPlaces]: 'places',
  [Item.Event]: 'events',
  [Item.Notifications]: 'notifications',
  [Item.ExperienceSubscription]: 'experience-subscriptions',
  [Item.Advanced]: 'advanced',
  [Item.AvatarCreationToken]: 'avatar-creation-tokens',
  [Item.ReferralRewards]: 'referral-reward-details',
  [Item.Environment]: 'environments',
  [Item.Look]: 'look',
  [Item.Alert]: 'alerts',
};

export const itemTypeToThumbnailType: { [key in Item]: ThumbnailTypes } = {
  [Item.Game]: ThumbnailTypes.gameIcon,
  [Item.CatalogAsset]: ThumbnailTypes.assetThumbnail,
  [Item.LibraryAsset]: ThumbnailTypes.assetThumbnail,
  [Item.Bundle]: ThumbnailTypes.bundleThumbnail,
  [Item.Badge]: ThumbnailTypes.badgeIcon,
  [Item.GamePass]: ThumbnailTypes.gamePassIcon,
  [Item.DeveloperProduct]: ThumbnailTypes.developerProductIcon,
  [Item.TranslatorGame]: ThumbnailTypes.gameIcon,
  [Item.Places]: ThumbnailTypes.placeIcon,
  [Item.CreatedPlaces]: ThumbnailTypes.placeIcon,
  [Item.Event]: ThumbnailTypes.universeThumbnail,
  [Item.Notifications]: ThumbnailTypes.assetThumbnail,
  [Item.ExperienceSubscription]: ThumbnailTypes.assetThumbnail,
  [Item.Advanced]: ThumbnailTypes.assetThumbnail,
  [Item.AvatarCreationToken]: ThumbnailTypes.bundleThumbnail,
  [Item.ReferralRewards]: ThumbnailTypes.assetThumbnail,
  // Environments and Alert don't have thumbnails, but the type system requires something here,
  // because the code assumes all Items have thumbnails.
  [Item.Environment]: ThumbnailTypes.universeThumbnail,
  [Item.Alert]: ThumbnailTypes.universeThumbnail,

  // Look thumbnails are handled by a different batch endpoint, but the type requires something here.
  [Item.Look]: ThumbnailTypes.assetThumbnail,
};

export const itemTypeToReturnPolicyType: { [key in Item]: ReturnPolicy } = {
  [Item.Game]: ReturnPolicy.AutoGenerated,
  [Item.Bundle]: ReturnPolicy.AutoGenerated,
  [Item.CatalogAsset]: ReturnPolicy.PlaceHolder,
  [Item.LibraryAsset]: ReturnPolicy.AutoGenerated,
  [Item.Badge]: ReturnPolicy.AutoGenerated,
  [Item.GamePass]: ReturnPolicy.AutoGenerated,
  [Item.DeveloperProduct]: ReturnPolicy.AutoGenerated,
  [Item.TranslatorGame]: ReturnPolicy.PlaceHolder,
  [Item.Places]: ReturnPolicy.AutoGenerated,
  [Item.CreatedPlaces]: ReturnPolicy.AutoGenerated,
  [Item.Event]: ReturnPolicy.PlaceHolder,
  [Item.Notifications]: ReturnPolicy.AutoGenerated,
  [Item.ExperienceSubscription]: ReturnPolicy.AutoGenerated,
  [Item.Advanced]: ReturnPolicy.AutoGenerated,
  [Item.AvatarCreationToken]: ReturnPolicy.AutoGenerated,
  [Item.ReferralRewards]: ReturnPolicy.AutoGenerated,
  [Item.Environment]: ReturnPolicy.AutoGenerated,
  [Item.Look]: ReturnPolicy.AutoGenerated,
  [Item.Alert]: ReturnPolicy.AutoGenerated,
};

export const assetFullNameKeys: { [key in Asset]: string } = {
  [Asset.Place]: 'Label.Games',
  [Asset.MyExperiences]: 'Label.Games',
  [Asset.SharedExperiences]: 'Label.Games',
  [Asset.TShirt]: 'Label.TShirts',
  [Asset.Shirt]: 'Label.Shirts',
  [Asset.Pants]: 'Label.Pants',
  [Asset.Hat]: 'Label.Hats',
  [Asset.HairAccessory]: 'Label.HairAccessories',
  [Asset.FaceAccessory]: 'Label.FaceAccessories',
  [Asset.NeckAccessory]: 'Label.NeckAccessories',
  [Asset.ShoulderAccessory]: 'Label.ShoulderAccessories',
  [Asset.FrontAccessory]: 'Label.FrontAccessories',
  [Asset.BackAccessory]: 'Label.BackAccessories',
  [Asset.WaistAccessory]: 'Label.WaistAccessories',
  [Asset.TShirtAccessory]: 'Label.TShirts',
  [Asset.ShirtAccessory]: 'Label.Shirts',
  [Asset.PantsAccessory]: 'Label.Pants',
  [Asset.JacketAccessory]: 'Label.Jackets',
  [Asset.SweaterAccessory]: 'Label.Sweaters',
  [Asset.ShortsAccessory]: 'Label.ShortsAccessories',
  [Asset.DressSkirtAccessory]: 'Label.Skirts',
  [Asset.EmoteAnimation]: 'Label.Emote',
  [Asset.EyebrowAccessory]: 'Label.EyebrowAccessories',
  [Asset.EyelashAccessory]: 'Label.EyelashAccessories',
  [Asset.FaceMakeup]: 'Label.FaceMakeupAccessories',
  [Asset.LipMakeup]: 'Label.LipMakeupAccessories',
  [Asset.EyeMakeup]: 'Label.EyeMakeupAccessories',
  [Asset.AllCatalogAsset]: 'Label.AllAssetTypes',
  [Asset.Decal]: 'Label.Decals',
  [Asset.Image]: 'Label.Images',
  [Asset.Audio]: 'Label.Audios',
  [Asset.Model]: 'Label.Models',
  [Asset.Mesh]: 'Label.Meshes',
  [Asset.MeshPart]: 'Label.MeshParts',
  [Asset.Plugin]: 'Label.Plugins',
  [Asset.FontFamily]: 'Label.FontFamily',
  [Asset.Animation]: 'Label.Animations',
  [Asset.Video]: 'Label.Videos',
  [Asset.Event]: 'Label.Events',
  [Asset.PastEvent]: 'Label.PastEvents',
  [Asset.DraftEvent]: 'Label.DraftEvents',
  [Asset.UpcomingEvent]: 'Label.UpcomingEvents',
  [Asset.ShareLink]: 'Label.ShareLinks',
  [Asset.StorePreviewVideo]: 'Label.StorePreviewVideo',
  [Asset.GamePreviewVideo]: 'Label.GamePreviewVideo',
};
export const assetTypeToSingularNameKeys: { [key in Asset]: string } = {
  [Asset.Place]: 'Label.Games',
  [Asset.MyExperiences]: 'Label.Games',
  [Asset.SharedExperiences]: 'Label.Games',
  [Asset.TShirt]: 'Label.TShirt',
  [Asset.Shirt]: 'Label.Shirt',
  [Asset.Pants]: 'Label.Pants',
  [Asset.Hat]: 'Label.Hat',
  [Asset.HairAccessory]: 'Label.HairAccessory',
  [Asset.FaceAccessory]: 'Label.FaceAccessory',
  [Asset.NeckAccessory]: 'Label.NeckAccessory',
  [Asset.ShoulderAccessory]: 'Label.ShoulderAccessory',
  [Asset.FrontAccessory]: 'Label.FrontAccessory',
  [Asset.BackAccessory]: 'Label.BackAccessory',
  [Asset.WaistAccessory]: 'Label.WaistAccessory',
  [Asset.TShirtAccessory]: 'Label.TShirt',
  [Asset.ShirtAccessory]: 'Label.Shirt',
  [Asset.PantsAccessory]: 'Label.Pants',
  [Asset.JacketAccessory]: 'Label.Jacket',
  [Asset.SweaterAccessory]: 'Label.Sweater',
  [Asset.ShortsAccessory]: 'Label.ShortsAccessories',
  [Asset.DressSkirtAccessory]: 'Label.Skirt',
  [Asset.EmoteAnimation]: 'Label.Emote',
  [Asset.EyebrowAccessory]: 'Label.EyebrowAccessory',
  [Asset.EyelashAccessory]: 'Label.EyelashAccessory',
  [Asset.FaceMakeup]: 'Label.FaceMakeup',
  [Asset.LipMakeup]: 'Label.LipMakeup',
  [Asset.EyeMakeup]: 'Label.EyeMAkeup',
  [Asset.AllCatalogAsset]: 'Label.AllAssetTypes',
  [Asset.Decal]: 'Label.Decal',
  [Asset.Image]: 'Label.Image',
  [Asset.Audio]: 'Label.Audios',
  [Asset.Model]: 'Label.Model',
  [Asset.Mesh]: 'Label.Mesh',
  [Asset.MeshPart]: 'Label.MeshPart',
  [Asset.Plugin]: 'Label.Plugin',
  [Asset.Animation]: 'Label.Animation',
  [Asset.FontFamily]: 'Label.FontFamily',
  [Asset.Video]: 'Label.Video',
  [Asset.Event]: 'Label.Event',
  [Asset.PastEvent]: 'Label.PastEvent',
  [Asset.DraftEvent]: 'Label.DraftEvent',
  [Asset.UpcomingEvent]: 'Label.UpcomingEvent',
  [Asset.ShareLink]: 'Label.ShareLink',
  [Asset.StorePreviewVideo]: 'Label.StorePreviewVideo',
  [Asset.GamePreviewVideo]: 'Label.GamePreviewVideo',
};

export const itemFullNameKeys: { [key in Item]: string } = {
  [Item.Game]: 'Label.Games',
  [Item.CatalogAsset]: 'Label.AvatarItems',
  [Item.Bundle]: 'Label.AvatarItem',
  [Item.LibraryAsset]: 'Label.DevelopmentItems',
  [Item.Badge]: 'Label.Badges',
  [Item.GamePass]: 'Label.GamePasses',
  [Item.DeveloperProduct]: 'Label.DeveloperProducts',
  [Item.TranslatorGame]: 'Label.TranslatorGame',
  [Item.Places]: 'Label.Places',
  [Item.CreatedPlaces]: 'Label.Places',
  [Item.Event]: 'Label.Events',
  [Item.Notifications]: 'Label.Notifications',
  [Item.ExperienceSubscription]: 'Label.Subscriptions',
  [Item.Advanced]: 'Header.Title',
  [Item.AvatarCreationToken]: 'Label.AvatarCreationTokens',
  [Item.ReferralRewards]: 'Label.ReferralRewards',
  [Item.Environment]: 'Label.Environments',
  [Item.Look]: 'Label.Looks',
  [Item.Alert]: 'Heading.Alerts',
};

export const itemTypeToNameKeys: { [key in Item]: string } = {
  [Item.Game]: 'Label.Game',
  [Item.CatalogAsset]: 'Label.AvatarItem',
  [Item.Bundle]: 'Label.AvatarItem',
  [Item.LibraryAsset]: 'Label.DevelopmentItem',
  [Item.Badge]: 'Label.Badge',
  [Item.GamePass]: 'Label.GamePass',
  [Item.DeveloperProduct]: 'Label.DeveloperProduct',
  [Item.TranslatorGame]: 'Label.TranslatorGame',
  [Item.Places]: 'Label.Place',
  [Item.CreatedPlaces]: 'Label.Places',
  [Item.Event]: 'Label.Event',
  [Item.Notifications]: 'Label.Notifications',
  [Item.ExperienceSubscription]: 'Label.Subscription',
  [Item.Advanced]: 'Header.Title',
  [Item.AvatarCreationToken]: 'Label.Token',
  [Item.ReferralRewards]: 'Label.ReferralRewards',
  [Item.Environment]: 'Label.Environment',
  [Item.Look]: 'Label.Looks',
  [Item.Alert]: 'Heading.Alert',
};

export const itemTypeToSingularNameKeys: { [key in Item]: string } = {
  [Item.Game]: 'Heading.Game',
  [Item.CatalogAsset]: 'Heading.AvatarItem',
  [Item.Bundle]: 'Heading.AvatarItem',
  [Item.LibraryAsset]: 'Heading.DevelopmentItem',
  [Item.Badge]: 'Heading.Badge',
  [Item.GamePass]: 'Heading.Pass',
  [Item.DeveloperProduct]: 'Heading.DeveloperProduct',
  [Item.TranslatorGame]: 'Heading.TranslatorGame',
  [Item.Places]: 'Heading.Place',
  [Item.CreatedPlaces]: 'Heading.Place',
  [Item.Event]: 'Heading.Event',
  [Item.Notifications]: 'Heading.Notifications.CreateContent',
  [Item.ExperienceSubscription]: 'Heading.Subscription',
  [Item.Advanced]: 'Header.Title',
  [Item.AvatarCreationToken]: 'Label.AvatarCreationToken',
  [Item.ReferralRewards]: 'Heading.ReferralRewards',
  [Item.Environment]: 'Heading.Environment',
  [Item.Look]: 'Heading.Looks',
  [Item.Alert]: 'Heading.Alert',
};

export const itemTypeToCreatePath: { [key in Item]: string | null } = {
  [Item.Game]: null,
  [Item.CatalogAsset]: null,
  [Item.Bundle]: null,
  [Item.LibraryAsset]: null,
  [Item.Badge]: 'badges/create',
  [Item.GamePass]: 'passes/create',
  [Item.DeveloperProduct]: 'developer-products/create',
  [Item.ReferralRewards]: 'referral-reward-details/create',
  [Item.TranslatorGame]: null,
  [Item.Places]: null,
  [Item.CreatedPlaces]: null,
  [Item.Event]: 'events/create',
  [Item.Notifications]: null,
  [Item.ExperienceSubscription]: 'experience-subscriptions/create',
  [Item.Advanced]: null,
  [Item.AvatarCreationToken]: 'avatar-creation-tokens/create',
  [Item.Environment]: 'environments/create',
  [Item.Look]: 'looks/create',
  [Item.Alert]: 'alerts/create',
};

export const itemTypeToReorderPath: { [key in Item]: string | null } = {
  [Item.Game]: null,
  [Item.CatalogAsset]: null,
  [Item.Bundle]: null,
  [Item.LibraryAsset]: null,
  [Item.Badge]: 'badges/reorder',
  [Item.ReferralRewards]: null,
  [Item.GamePass]: null,
  [Item.DeveloperProduct]: null,
  [Item.TranslatorGame]: null,
  [Item.Places]: null,
  [Item.CreatedPlaces]: null,
  [Item.Event]: null,
  [Item.Notifications]: null,
  [Item.ExperienceSubscription]: null,
  [Item.Advanced]: null,
  [Item.AvatarCreationToken]: null,
  [Item.Environment]: null,
  [Item.Look]: null,
  [Item.Alert]: null,
};

export const itemTypeToLearnMoreUrl: { [key in Item]?: string } = {
  [Item.Badge]: BADGE_LEARN_MORE_URL,
  [Item.DeveloperProduct]: DEVELOPER_PRODUCT_LEARN_MORE_URL,
  [Item.GamePass]: PASS_LEARN_MORE_URL,
  [Item.CatalogAsset]: SALE_LOCATION_LEARN_MORE_URL,
  [Item.ExperienceSubscription]: SUBSCRIPTION_LEARN_MORE_URL,
  [Item.AvatarCreationToken]: TOKEN_LEARN_MORE_URL,
  [Item.Look]: undefined,
};

export const uninitializedUniverseId = -1;
export const numSecondsInOneYear = 31536000;
export const numSecondsInOneWeek = 604800;
export const numDaysInOneWeek = 7;
export const toastDurationTime = 3000;
export const AllSettlePromiseSuccess = 'fulfilled';
export const AllSettlePromiseFailed = 'rejected';
